import localforage from "localforage";

const FOLLOWS_KEY = "hp_followed_podcasts";
const PLAYLIST_KEY = "hp_playlist";
const LISTENING_KEY = "hp_listening_progress";

/** The most the API takes in one import; the newest progress and the start of the playlist are kept */
const MAX_FOLLOWS = 500;
const MAX_PLAYLIST = 200;
const MAX_LISTENING = 500;

export interface ImportBody {
    follows: number[];
    playlist: number[];
    listening: Array<{
        episode_id: number;
        position: number;
        duration: number;
        played: boolean;
        updated_at: string;
    }>;
}

export interface ImportCounts {
    follows: number;
    playlist: number;
    listening: number;
}

export interface ImportResult {
    imported: ImportCounts;
    skipped: ImportCounts;
}

const isId = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value) && value > 0;

const readList = async (key: string): Promise<unknown[]> => {
    try {
        const value: unknown = await localforage.getItem(key);
        return Array.isArray(value) ? value : [];
    } catch {
        return [];
    }
};

const unique = (ids: number[]) => [...new Set(ids)];

const readListening = (): ImportBody["listening"] => {
    try {
        const raw = localStorage.getItem(LISTENING_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        if (!parsed || typeof parsed !== "object") return [];

        const entries: Array<ImportBody["listening"][number] & { at: number }> = [];
        for (const [key, value] of Object.entries(parsed)) {
            const entry = value as Partial<Record<"position" | "duration" | "played" | "updatedAt", unknown>> | null;
            const id = Number(key);
            if (
                !isId(id) ||
                !entry ||
                typeof entry.position !== "number" ||
                typeof entry.duration !== "number" ||
                typeof entry.updatedAt !== "number" ||
                !Number.isFinite(entry.updatedAt)
            ) {
                continue;
            }
            entries.push({
                episode_id: id,
                position: entry.position,
                duration: entry.duration,
                played: entry.played === true,
                updated_at: new Date(entry.updatedAt).toISOString(),
                at: entry.updatedAt,
            });
        }
        return entries
            .sort((a, b) => b.at - a.at)
            .slice(0, MAX_LISTENING)
            .map(({ at: _at, ...entry }) => entry);
    } catch {
        return [];
    }
};

/** What this device kept from before there were accounts, in the shape `POST /me/import` takes */
export const readLocalData = async (): Promise<ImportBody> => {
    const [follows, playlist] = await Promise.all([readList(FOLLOWS_KEY), readList(PLAYLIST_KEY)]);
    return {
        // the follows kept from before accounts; those followed without an account are asked about separately
        follows: unique(follows.filter(isId)).slice(0, MAX_FOLLOWS),
        playlist: unique(
            playlist.map((item) => (item as { episode?: { id?: unknown } } | null)?.episode?.id).filter(isId)
        ).slice(0, MAX_PLAYLIST),
        listening: readListening(),
    };
};

export const isEmpty = (body: ImportBody) =>
    body.follows.length === 0 && body.playlist.length === 0 && body.listening.length === 0;

/** Forget what was imported, once the account has it */
export const clearLocalData = async () => {
    try {
        await Promise.all([localforage.removeItem(FOLLOWS_KEY), localforage.removeItem(PLAYLIST_KEY)]);
    } catch {
        // Blocked storage has nothing to clear
    }
    try {
        localStorage.removeItem(LISTENING_KEY);
    } catch {
        // Same
    }
};
